import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { Duration } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { NerdDatePicker } from "../nerdDatePicker";
import { NerdTimePicker } from "../nerdTimePicker";
import { durationFromString, } from "../nerdTimePicker/nerdTimePicker";
import { tz } from "moment-timezone";
import { Schedule, SwapVert } from "@mui/icons-material";
import { NerdVirtualizedAutocomplete } from "../nerdVirtualizedAutocomplete";
import { NerdDateRangePicker } from "../nerdDateRangePicker";
import { useTranslation } from "react-i18next";
import nerdUII18n from "../i18n";
/**
 *
 * @param {NerdDateTimeRangePickerProps} props component props
 * @returns {ReactElement} NerdDateTimeRangePicker
 */
export default function NerdDateTimeRangePicker(props) {
    const { endDate, startDate, onChange, startDatePickerProps, startTimePickerProps, endDatePickerProps, endTimePickerProps, timeFormat = "12", disableTimeZones, disableAllDay, DialogProps, disableVirtualizedAutocomplete, nerdDateRangePickerProps, defaultTimeZone, } = props;
    const [allDay, setAllDay] = useState(false);
    const timeZones = useMemo(getTimeZones, []);
    const currentUserTimeZone = useMemo(() => timeZones.find((el) => el.value === (defaultTimeZone ? defaultTimeZone : tz.guess())), [timeZones]);
    const [separateTimeZones, setSeparateTimeZones] = useState(false);
    const [diff, setDiff] = useState(startDate && endDate ? startDate.diff(endDate) : Duration.fromMillis(0));
    const [openTimeZoneDialog, setOpenTimeZoneDialog] = useState(false);
    const [isSameDate, setIsSameDate] = useState();
    const [startTime, setStartTime] = useState({
        value: startDate?.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm") || "",
        duration: Duration.fromObject({
            hours: startDate?.hour || 0,
            minutes: startDate?.minute || 0,
        }),
        isValid: startDate?.isValid || true,
    });
    const [endTime, setEndTime] = useState({
        value: endDate?.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm") || "",
        duration: Duration.fromObject({
            hours: endDate?.hour || 0,
            minutes: endDate?.minute || 0,
        }),
        isValid: endDate?.isValid || true,
    });
    const [isEndDateBefore, setIsEndDateBefore] = useState(endTime > startTime);
    const [inputStartTimeZone, setInputStartTimeZone] = useState(currentUserTimeZone);
    const [inputEndTimeZone, setInputEndTimeZone] = useState(currentUserTimeZone);
    const [startTimeZone, setStartTimeZone] = useState(currentUserTimeZone);
    const [endTimeZone, setEndTimeZone] = useState(currentUserTimeZone);
    const [separateTimeZonesInputValue, setSeparateTimeZonesInputValue] = useState(false);
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation("components", { i18n: nerdUII18n });
    /**
     *
     * @returns {Array<{label: string; value: string; offset: number;}>} List of timezones
     */
    function getTimeZones() {
        return tz
            .names()
            .map((_tz) => {
            let offset = tz.zone(_tz)?.utcOffset(Date.now());
            if (offset !== undefined) {
                offset = offset * -1;
                const hours = Math.floor(offset / 60);
                const minutes = Math.abs(offset % 60);
                return { hours, minutes, tz: _tz, offset };
            }
            else
                return { hours: NaN, minutes: NaN, tz: "", offset: 0 };
        })
            .filter((element) => !Number.isNaN(element.hours))
            .sort((a, b) => {
            if (a.hours < 0 && a.hours === b.hours)
                return b.minutes - a.minutes;
            else
                return a.offset - b.offset;
        })
            .map((el) => {
            return {
                label: `(UTC${el.hours.toLocaleString("en-US", {
                    signDisplay: "always",
                })}:${el.minutes.toLocaleString("en-US", {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                })}) ${el.tz}`,
                value: el.tz,
                offset: el.offset,
            };
        });
    }
    /**
     *
     * @param {DateTime} startDate Start date
     * @param {DateTime} endDate End date
     * @returns {DateTime[]} Dates with time zones
     */
    function getDatesWithTimeZones(startDate, endDate) {
        return [
            startDate.toUTC(inputStartTimeZone?.offset, { keepLocalTime: true }),
            endDate.toUTC(inputEndTimeZone?.offset, { keepLocalTime: true }),
        ];
    }
    /**
     *
     * @param {NerdTimeObject} time New End Time
     */
    function endTimeChange(time) {
        if (startDate && endDate) {
            let day = 0;
            if (isSameDate)
                if ((time.duration &&
                    time.duration.hours < startDate.hour &&
                    startDate.hour !== 0) ||
                    (time.duration &&
                        time.duration.hours === startDate.hour &&
                        time.duration.minutes < startDate.minute))
                    day = 1;
                else if (startDate.day !== endDate.day)
                    day = -1;
            const _endDate = endDate
                .set({
                hour: time.duration?.hours,
                minute: time.duration?.minutes,
            })
                .plus({
                day,
            });
            const [start, end] = getDatesWithTimeZones(startDate, _endDate);
            const _diff = end.diff(start);
            setDiff(_diff);
            const isBefore = isEndDateBeforeStartDate(_diff);
            if (!isBefore)
                onChange(start, end);
        }
    }
    /**
     *
     * @param {DateTime} date New Date
     */
    function endDateChange(date) {
        if (endDate && startDate) {
            const newDate = endDate.set({
                year: date.year,
                month: date.month,
                day: date.day,
            });
            const [start, end] = getDatesWithTimeZones(startDate, newDate);
            if (start.toISODate() === end.toISODate()) {
                setDiff(Duration.fromMillis(0));
                setEndTime(startTime);
                onChange(start, start);
                return;
            }
            const _diff = end.diff(start);
            setDiff(_diff);
            const isBeforeStart = isEndDateBeforeStartDate(_diff);
            if (!isBeforeStart) {
                onChange(start, end);
            }
        }
        else if (endDate) {
            const newDate = endDate.set({
                year: date.year,
                month: date.month,
                day: date.day,
            });
            onChange(startDate, newDate);
        }
        else {
            onChange(startDate, date);
        }
    }
    /**
     * Close timezones dialog
     */
    function closeTimeZonesDialog() {
        setOpenTimeZoneDialog(false);
    }
    /**
     * Change to current time zone
     */
    function useCurrentTimeZone() {
        closeTimeZonesDialog();
        setSeparateTimeZonesInputValue(false);
        setSeparateTimeZones(false);
        setInputStartTimeZone(currentUserTimeZone);
        setInputEndTimeZone(currentUserTimeZone);
        setEndTimeZone(currentUserTimeZone);
        setStartTimeZone(currentUserTimeZone);
        if (startDate && endDate)
            onChange(startDate.toUTC(currentUserTimeZone?.offset, {
                keepLocalTime: true,
            }), endDate.toUTC(currentUserTimeZone?.offset, {
                keepLocalTime: true,
            }));
        else if (startDate)
            onChange(startDate.toUTC(currentUserTimeZone?.offset, {
                keepLocalTime: true,
            }), endDate);
        else if (endDate)
            onChange(startDate, endDate.toUTC(currentUserTimeZone?.offset, {
                keepLocalTime: true,
            }));
    }
    /**
     * Swap end and start timezones
     */
    function swapTimeZones() {
        const tmp = inputStartTimeZone;
        setInputStartTimeZone(inputEndTimeZone);
        setInputEndTimeZone(tmp);
    }
    /**
     * Save timezones
     */
    function saveTimeZones() {
        closeTimeZonesDialog();
        if (startDate && endDate) {
            const [start, end] = getDatesWithTimeZones(startDate, endDate);
            const _diff = end.diff(start);
            const isBefore = isEndDateBeforeStartDate(_diff);
            if (!isBefore)
                onChange(start, end);
        }
        if (!separateTimeZonesInputValue) {
            setInputEndTimeZone(inputStartTimeZone);
            setEndTimeZone(inputStartTimeZone);
        }
        else
            setEndTimeZone(inputEndTimeZone);
        setStartTimeZone(inputStartTimeZone);
        setSeparateTimeZones(separateTimeZonesInputValue);
    }
    /**
     * Cancel timezones dialog
     */
    function cancelTimeZonesDialog() {
        closeTimeZonesDialog();
        setInputEndTimeZone(endTimeZone);
        setInputStartTimeZone(startTimeZone);
        setSeparateTimeZonesInputValue(separateTimeZones);
    }
    /**
     *
     * @param {DateTime} newStartDate New start date
     */
    function handleStartDateTimeChange(newStartDate) {
        if (newStartDate) {
            const newEndDate = newStartDate.plus(diff);
            const [start, end] = getDatesWithTimeZones(newStartDate, newEndDate);
            onChange(start, end);
            setStartTime({
                duration: durationFromString(newStartDate.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm"), timeFormat),
                isValid: true,
                value: newStartDate.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm"),
            });
            setEndTime({
                duration: durationFromString(newEndDate.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm"), timeFormat),
                isValid: true,
                value: newEndDate.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm"),
            });
        }
        else {
            onChange(newStartDate, endDate);
        }
    }
    /**
     *
     * @param {Duration} datesDuration Duration between end date and start date
     * @returns {boolean} True if end date is before start date
     */
    function isEndDateBeforeStartDate(datesDuration) {
        const diffAsHours = datesDuration.as("hours");
        if (Math.abs(diffAsHours) > 0 && diffAsHours < 0)
            return true;
        else
            return false;
    }
    useEffect(() => {
        if (startDate && endDate) {
            const [start, end] = getDatesWithTimeZones(startDate, endDate);
            setDiff(end.diff(start));
        }
    }, [startTimeZone, endTimeZone]);
    useEffect(() => {
        if ((startDate && endDate && startDate.toISODate() === endDate.toISODate()) ||
            (!startDate && !endDate)) {
            setIsSameDate(true);
        }
        else
            setIsSameDate(false);
    }, [startDate, endDate]);
    useEffect(() => {
        setIsEndDateBefore(isEndDateBeforeStartDate(diff));
    }, [diff]);
    useEffect(() => {
        if (endDate && startDate) {
            const endOffset = endDate.offset;
            const startOffset = startDate.offset;
            let startTz = currentUserTimeZone;
            let endTz = currentUserTimeZone;
            if (startOffset !== currentUserTimeZone?.offset)
                startTz = timeZones.find((tz) => tz.offset === startOffset);
            if (endOffset !== currentUserTimeZone?.offset)
                endTz = timeZones.find((tz) => tz.offset === endOffset);
            if (endOffset !== startOffset) {
                setSeparateTimeZones(true);
                setSeparateTimeZonesInputValue(true);
            }
            setInputEndTimeZone(endTz);
            setEndTimeZone(endTz);
            setStartTimeZone(startTz);
            setInputStartTimeZone(startTz);
        }
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs(Grid, { container: true, spacing: 1, gap: { xs: 1, md: 0 }, children: [_jsx(Grid, { item: true, xs: 1, md: 1, mt: 2, children: _jsx(Schedule, { sx: { color: "action.active" } }) }), _jsx(Grid, { item: true, xs: 10, md: 11, children: _jsxs(Grid, { container: true, spacing: 1, children: [!allDay ? (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, order: 1, children: _jsx(NerdDatePicker, { hideIcon: true, textFieldProps: {
                                                    label: " ",
                                                    "aria-label": t("nerdDatePicker.start_date"),
                                                }, ...startDatePickerProps, value: startDate, onChange: (date) => {
                                                    const newDate = startDate?.set({
                                                        year: date?.year,
                                                        month: date?.month,
                                                        day: date?.day,
                                                    }) || date;
                                                    handleStartDateTimeChange(newDate);
                                                    startDatePickerProps?.onChange?.(newDate);
                                                } }) }), _jsx(Grid, { item: true, order: 2, children: _jsx(NerdTimePicker, { ...startTimePickerProps, textFieldProps: {
                                                    label: " ",
                                                    "aria-label": t("nerdDateTimePicker.start_time"),
                                                    ...startTimePickerProps?.textFieldProps,
                                                    disabled: !startDate ? true : false,
                                                }, onChange: (time) => {
                                                    setStartTime(time);
                                                    if (time.isValid && startDate) {
                                                        const newStartDate = startDate?.set({
                                                            hour: time.duration?.hours,
                                                            minute: time.duration?.minutes,
                                                        });
                                                        handleStartDateTimeChange(newStartDate);
                                                    }
                                                    startTimePickerProps?.onChange?.(time);
                                                }, value: startTime.value, format: timeFormat }) }), separateTimeZones && (_jsx(Grid, { item: true, order: 3, mt: 1, children: _jsx(Button, { color: "inherit", sx: {
                                                    fontSize: theme.typography.body1.fontSize,
                                                    fontWeight: theme.typography.body1.fontWeight,
                                                    display: "block",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: 180,
                                                    textOverflow: "ellipsis",
                                                    overflow: "hidden",
                                                }, onClick: () => setOpenTimeZoneDialog(true), children: startTimeZone?.label }) })), !separateTimeZones && (_jsx(Grid, { item: true, mt: 1, order: 3, children: _jsx(Typography, { fontSize: 24, children: "-" }) })), _jsx(Grid, { item: true, order: separateTimeZones ? 5 : 4, children: _jsx(NerdTimePicker, { showDiff: isSameDate, ...endTimePickerProps, textFieldProps: {
                                                    label: " ",
                                                    "aria-label": t("nerdDateTimePicker.end_time"),
                                                    ...endTimePickerProps?.textFieldProps,
                                                    disabled: !startDate &&
                                                        !(isSameDate === false || separateTimeZones)
                                                        ? true
                                                        : false,
                                                    error: isEndDateBefore,
                                                }, startTime: isSameDate && startDate
                                                    ? startDate.toFormat(timeFormat === "12" ? "hh:mm a" : "HH:mm")
                                                    : undefined, value: endTime.value, onChange: (time) => {
                                                    setEndTime(time);
                                                    if (time.isValid)
                                                        endTimeChange(time);
                                                    endTimePickerProps?.onChange?.(time);
                                                }, format: timeFormat }) }), (isSameDate === false || separateTimeZones) && (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, order: separateTimeZones ? 4 : 5, children: _jsx(NerdDatePicker, { datePickerProps: {
                                                            minDate: startDate || undefined,
                                                        }, textFieldProps: {
                                                            label: " ",
                                                            "aria-label": t("nerdDatePicker.end_date"),
                                                            error: isEndDateBefore,
                                                        }, hideIcon: true, ...endDatePickerProps, onChange: (date) => {
                                                            if (date !== null)
                                                                endDateChange(date);
                                                            endDatePickerProps?.onChange?.(date);
                                                        }, value: endDate }) }), separateTimeZones && (_jsx(Grid, { item: true, order: 6, mt: 1, children: _jsx(Button, { color: "inherit", sx: {
                                                            fontSize: theme.typography.body1.fontSize,
                                                            fontWeight: theme.typography.body1.fontWeight,
                                                            display: "block",
                                                            whiteSpace: "nowrap",
                                                            maxWidth: 180,
                                                            textOverflow: "ellipsis",
                                                            overflow: "hidden",
                                                        }, onClick: () => setOpenTimeZoneDialog(true), children: endTimeZone?.label }) }))] })), isEndDateBefore && (_jsx(Grid, { item: true, xs: 12, order: 7, children: _jsx(Typography, { color: theme.palette.error.main, variant: "caption", children: t("nerdDateTimeRangePicker.end_date_later_start_date") }) }))] })) : (_jsx(Grid, { item: true, order: 1, children: _jsx(NerdDateRangePicker, { endTextFieldProps: {
                                            label: " ",
                                        }, startTextFieldProps: {
                                            label: " ",
                                        }, hideIcon: true, ...nerdDateRangePickerProps, value: [
                                            startDate?.toLocal() || null,
                                            endDate?.toLocal() || null,
                                        ], onChange: (newValue) => {
                                            if (newValue[0] !== null && newValue[1] !== null)
                                                onChange(newValue[0].toLocal(), newValue[1].toLocal());
                                        } }) })), _jsx(Grid, { item: true, xs: 12, order: 8, children: _jsxs(Grid, { container: true, alignItems: "center", children: [!disableAllDay && (_jsx(Grid, { item: true, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: allDay, onChange: (e) => {
                                                            const checked = e.target.checked;
                                                            setAllDay(checked);
                                                            if (startDate && endDate) {
                                                                if (checked) {
                                                                    const resetTime = {
                                                                        hour: 0,
                                                                        minute: 0,
                                                                        millisecond: 0,
                                                                        second: 0,
                                                                    };
                                                                    onChange(startDate.toLocal().set(resetTime), endDate.toLocal().set(resetTime));
                                                                }
                                                                else {
                                                                    const [start, end] = getDatesWithTimeZones(startDate, endDate);
                                                                    if (!isEndDateBefore)
                                                                        onChange(start.set({
                                                                            hour: startTime.duration?.hours,
                                                                            minute: startTime.duration?.minutes,
                                                                        }), end.set({
                                                                            hour: endTime.duration?.hours,
                                                                            minute: endTime.duration?.minutes,
                                                                        }));
                                                                }
                                                            }
                                                        } }), label: t("nerdDateTimeRangePicker.all_day") }) })), !allDay && !disableTimeZones && (_jsx(Grid, { item: true, children: _jsx(Button, { color: "inherit", sx: {
                                                        fontSize: theme.typography.body1.fontSize,
                                                        fontWeight: theme.typography.body1.fontWeight,
                                                    }, onClick: () => setOpenTimeZoneDialog(true), children: t("nerdDateTimeRangePicker.time_zone") }) }))] }) })] }) })] }), !disableTimeZones && (_jsxs(Dialog, { open: openTimeZoneDialog, fullScreen: isDownMd, ...DialogProps, onClose: (e, reason) => {
                    closeTimeZonesDialog();
                    DialogProps?.onClose?.(e, reason);
                }, children: [_jsx(DialogTitle, { children: t("nerdDateTimeRangePicker.time_zone") }), _jsx(DialogContent, { children: _jsxs(Grid, { container: true, gap: 1, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: separateTimeZonesInputValue, onChange: (e) => {
                                                setSeparateTimeZonesInputValue(e.target.checked);
                                                setInputEndTimeZone(inputStartTimeZone);
                                            } }), label: t("nerdDateTimeRangePicker.separate_time_zones") }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Grid, { container: true, gap: 1, alignItems: "center", children: [_jsx(Grid, { item: true, xs: 8, children: _jsxs(Grid, { container: true, gap: 1, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(NerdVirtualizedAutocomplete, { disableVirtualization: disableVirtualizedAutocomplete, multiple: false, sx: {
                                                                    width: "100%",
                                                                }, disableClearable: true, onChange: (_event, value) => {
                                                                    if (typeof value !== "string") {
                                                                        setInputStartTimeZone(value);
                                                                        if (!separateTimeZonesInputValue)
                                                                            setInputEndTimeZone(value);
                                                                    }
                                                                }, value: inputStartTimeZone, getOptionLabel: (option) => {
                                                                    if (typeof option !== "string")
                                                                        return option.label;
                                                                    else
                                                                        return option;
                                                                }, renderOption: (props, option) => {
                                                                    if (!disableVirtualizedAutocomplete)
                                                                        return [props, option.label];
                                                                    else
                                                                        return (_jsx(MenuItem, { ...props, children: option.label }));
                                                                }, options: timeZones, renderInput: (params) => {
                                                                    return (_jsx(TextField, { ...params, label: t("nerdDateTimeRangePicker.start_tz"), variant: "standard" }));
                                                                } }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(NerdVirtualizedAutocomplete, { disableVirtualization: disableVirtualizedAutocomplete, multiple: false, sx: {
                                                                    width: "100%",
                                                                }, disableClearable: true, disabled: !separateTimeZonesInputValue, onChange: (_event, value) => {
                                                                    if (typeof value !== "string") {
                                                                        setInputEndTimeZone(value);
                                                                    }
                                                                }, value: inputEndTimeZone, options: timeZones, getOptionLabel: (option) => {
                                                                    if (typeof option !== "string")
                                                                        return option.label;
                                                                    else
                                                                        return option;
                                                                }, renderOption: (props, option) => {
                                                                    if (!disableVirtualizedAutocomplete)
                                                                        return [props, option.label];
                                                                    else
                                                                        return (_jsx(MenuItem, { ...props, children: option.label }));
                                                                }, renderInput: (params) => {
                                                                    return (_jsx(TextField, { ...params, label: t("nerdDateTimeRangePicker.end_tz"), variant: "standard" }));
                                                                } }) })] }) }), _jsx(Grid, { item: true, children: _jsx(Tooltip, { title: t("nerdDateTimeRangePicker.swap_time_zones"), children: _jsx("span", { children: _jsx(IconButton, { "aria-label": t("nerdDateTimeRangePicker.swap_time_zones"), disabled: separateTimeZones || separateTimeZonesInputValue
                                                                ? false
                                                                : true, onClick: swapTimeZones, children: _jsx(SwapVert, {}) }) }) }) })] }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: useCurrentTimeZone, color: "inherit", children: t("nerdDateTimeRangePicker.use_current_time_zone") }), _jsx(Button, { onClick: cancelTimeZonesDialog, color: "secondary", children: t("common:global.cancel") }), _jsx(Button, { onClick: saveTimeZones, children: t("common:global.ok") })] })] }))] }));
}
